import { Fragment, useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom"; // <-- Grab router context
import OverallBudgetCard from "../../../../../components/ui/cards/finances/OverallBudgetCard";
import DataTable from "../../../../../components/ui/table/DataTable";
import { useContentHeightCalculation } from "../../../../../hooks/useContentHeightCalculation";
import PersonalFinancesMenu from "./common/PersonalFinancesMenu";
import FilterModal from "../../../../../components/ui/popup/FilterModal";
import BudgetDetailsModal from "../../../../../components/ui/popup/finances/BudgetDetailsModal";
import {
  createBudget,
  deleteBudget,
  fetchBudgets,
} from "../../../../../api/finances/budgetAPI";
import LoadingPlaceholder from "../../../../../components/ui/loading/LoadingPlaceholder";
import CreateBudgetModal from "../../../../../components/ui/popup/finances/CreateBudgetModal";
import ConfirmationModal from "../../../../../components/ui/popup/ConfirmationModal";
import GhostDataTable from "../../../../../components/ui/loading/skeletons/GhostDataTable";
import GhostOverallBudgetCard from "../../../../../components/ui/loading/skeletons/GhostOverallBudgetCard";
import GhostCardContainer from "../../../../../components/ui/loading/skeletons/GhostCardContainer";
import { useRefreshOnAuth } from "../../../../../hooks/useRefreshOnAuth";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import { primarySpendingCategories } from "../../../../../utils/constants/plaidCategoryTaxonomy";
import {
  budgetMatchesFilters,
  getAccountId,
  getAccountLabel,
  getInstitutionId,
  getInstitutionLabel,
} from "../../../../../components/ui/popup/finances/utils/budgetHelpers";
import InstitutionDataLoaderBanner from "./common/InsitutionDataLoaderBanner"; // <-- Import Onboarding Loader Card
import InstitutionPartialDataWarningBanner from "./common/InstitutionPartialDataWarningBanner";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";
import { FILTER_SCHEMA } from "../../../../../utils/schemas/language/components/filterSchema";
import { fetchInstitutionsAccounts } from "../../../../../api/finances/plaidConnectivityAPI";

function PersonalBudgets() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const filterContent = getLanguageContent(FILTER_SCHEMA);
  const { contentRef, mainRef } = useContentHeightCalculation();
  const [overallFrequency, setOverallFrequency] = useState("ALL");

  // ============================================================
  // PIPELINE SYNC STATE EXTRACTION & CONDITION EVALUATIONS
  // ============================================================
  const {
    institutionSyncStates = [],
    loadSyncStates,
    isLoadingSyncStates,
  } = useOutletContext() || {};

  const isEmptyOnboarding = useMemo(() => {
    if (isLoadingSyncStates) return true;
    if (institutionSyncStates.length === 0) return false;
    return institutionSyncStates.every(
      (inst) => inst.transaction_first_load_completed === false,
    );
  }, [institutionSyncStates, isLoadingSyncStates]);

  const isPartialSyncing = useMemo(() => {
    if (
      isLoadingSyncStates ||
      isEmptyOnboarding ||
      institutionSyncStates.length === 0
    )
      return false;
    return institutionSyncStates.some(
      (inst) => inst.transaction_history_completed === false,
    );
  }, [institutionSyncStates, isEmptyOnboarding, isLoadingSyncStates]);

  const processingInstitutionsText = useMemo(() => {
    const pendingNames = institutionSyncStates
      .filter((inst) => inst.transaction_history_completed === false)
      .map((inst) => inst.institution_name)
      .filter(Boolean);

    if (pendingNames.length === 0) return "";
    if (pendingNames.length === 1) return pendingNames[0];
    if (pendingNames.length === 2)
      return `${pendingNames[0]} & ${pendingNames[1]}`;
    return `${pendingNames.slice(0, -1).join(", ")}, & ${
      pendingNames[pendingNames.length - 1]
    }`;
  }, [institutionSyncStates]);

  // Data States
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState([]);
  const [summary, setSummary] = useState({
    all: { total_limit: 0, total_spent: 0, percentage: 0 },
    weekly: { total_limit: 0, total_spent: 0, percentage: 0 },
    monthly: { total_limit: 0, total_spent: 0, percentage: 0 },
  });

  const activeSummary = useMemo(() => {
    if (!summary) return { total_limit: 0, total_spent: 0, percentage: 0 };

    const key = overallFrequency.toLowerCase(); // "all" | "weekly" | "monthly"

    // Return key-specific object or fall back gracefully
    return (
      summary[key] ||
      summary.all || { total_limit: 0, total_spent: 0, percentage: 0 }
    );
  }, [summary, overallFrequency]);

  // Modal/UI States
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [isModalBudgetOpen, setIsModalBudgetOpen] = useState(false);
  const [isCreateBudgetModalOpen, setIsCreateBudgetModalOpen] = useState(false);

  // Filter States
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    institutions: "",
    accounts: "",
    categories: "",
    frequency: "",
  });
  const [activeFilters, setActiveFilters] = useState({
    institutions: "",
    accounts: "",
    categories: "",
    frequency: "",
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [createBudgetError, setCreateBudgetError] = useState("");
  const [isSavingBudget, setIsSavingBudget] = useState(false);

  const headers = [
    {
      label: content.budgeting.budgets.headers.name,
      key: "name",
      sortable: false,
      width: "160px",
    },
    {
      label: content.budgeting.budgets.headers.frequency,
      key: "frequency",
      sortable: false,
      width: "160px",
    },
    {
      label: content.budgeting.budgets.headers.spent_vs_limit,
      key: "spentlimit",
      sortable: false,
      width: "340px",
    },
  ];

  const promptDeleteBudget = (budget) => {
    setDeleteError("");
    setBudgetToDelete(budget);
    setIsDeleteModalOpen(true);
  };

  const loadInitialData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    const [budgetRes, metadataRes] = await Promise.all([
      fetchBudgets(),
      fetchInstitutionsAccounts(),
    ]);

    if (budgetRes.success) {
      setRows(budgetRes.data.budgets);
      setSummary(budgetRes.data.summary);
    }
    if (metadataRes.success) {
      setMetadata(metadataRes.data);
    }
    if (showLoader) setLoading(false);
  }, []);

  useRefreshOnAuth(loadInitialData);

  const handleSaveNewBudget = async (formData) => {
    setCreateBudgetError("");
    setIsSavingBudget(true);

    const result = await createBudget(formData);

    if (result.success) {
      await loadInitialData(false);
      setIsCreateBudgetModalOpen(false);
      setCreateBudgetError("");
    } else {
      setCreateBudgetError(result.message);
    }

    setIsSavingBudget(false);
  };

  const confirmDelete = async () => {
    if (!budgetToDelete) return;

    setDeleteError("");
    setIsDeleting(true);
    const result = await deleteBudget(budgetToDelete.uuid);

    if (result.success) {
      setRows((prev) => prev.filter((b) => b.uuid !== budgetToDelete.uuid));
      await loadInitialData(false);
      setIsDeleteModalOpen(false);
      setBudgetToDelete(null);
      setDeleteError("");
    } else {
      setDeleteError(result.message);
    }
    setIsDeleting(false);
  };

  const handleFilterClick = () => {
    setFilters(activeFilters);
    setIsFilterOpen(true);
  };

  const getRowsForFilterOptions = useCallback(
    (excludedField) => {
      return rows.filter((row) => {
        const scopedFilters = {
          institutions:
            excludedField === "institutions" ? "" : filters.institutions,
          accounts: excludedField === "accounts" ? "" : filters.accounts,
          categories: excludedField === "categories" ? "" : filters.categories,
          frequency: excludedField === "frequency" ? "" : filters.frequency,
        };

        return budgetMatchesFilters(row, scopedFilters);
      });
    },
    [filters, rows],
  );

  const institutionOptions = useMemo(() => {
    const optionsById = new Map();

    getRowsForFilterOptions("institutions").forEach((row) => {
      row.institutions.forEach((institution) => {
        const institutionId = getInstitutionId(institution);
        if (!institutionId || optionsById.has(String(institutionId))) return;

        optionsById.set(String(institutionId), {
          value: String(institutionId),
          label: getInstitutionLabel(institution, metadata),
        });
      });
    });

    return Array.from(optionsById.values()).sort((first, second) =>
      first.label.localeCompare(second.label),
    );
  }, [getRowsForFilterOptions, metadata]);

  const accountOptions = useMemo(() => {
    const optionsById = new Map();

    getRowsForFilterOptions("accounts").forEach((row) => {
      row.accounts.forEach((account) => {
        const accountId = getAccountId(account);
        if (!accountId || optionsById.has(String(accountId))) return;

        optionsById.set(String(accountId), {
          value: String(accountId),
          label: getAccountLabel(account, metadata),
        });
      });
    });

    return Array.from(optionsById.values()).sort((first, second) =>
      first.label.localeCompare(second.label),
    );
  }, [getRowsForFilterOptions, metadata]);

  const categoryOptions = useMemo(() => {
    const categoryCodes = new Set();

    getRowsForFilterOptions("categories").forEach((row) => {
      row.categories.forEach((category) => {
        categoryCodes.add(String(category).toUpperCase());
      });
    });

    return Array.from(categoryCodes)
      .map((categoryCode) => {
        const match = primarySpendingCategories.find(
          (category) => category.value === categoryCode,
        );

        return {
          value: categoryCode,
          label: match?.label || categoryCode,
        };
      })
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [getRowsForFilterOptions]);

  const frequencyOptions = useMemo(() => {
    const frequencies = new Set();

    getRowsForFilterOptions("frequency").forEach((row) => {
      if (row.frequency) {
        frequencies.add(row.frequency);
      }
    });

    return Array.from(frequencies)
      .map((frequency) => ({ value: frequency, label: frequency }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [getRowsForFilterOptions]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const nextFilters = {
        ...prev,
        [field]: value,
        ...(field === "institutions" ? { accounts: "" } : {}),
      };

      const hasMatchingRows = rows.some((row) =>
        budgetMatchesFilters(row, nextFilters),
      );

      return hasMatchingRows
        ? nextFilters
        : {
            ...nextFilters,
            categories: "",
          };
    });
  };

  const handleApplyFilters = () => {
    setActiveFilters(filters);
    setIsFilterOpen(false);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => budgetMatchesFilters(row, activeFilters));
  }, [activeFilters, rows]);

  const getBudgetActions = (row) => [
    {
      label: content.budgeting.budgets.actions.detail,
      onClick: () => {
        setSelectedBudget(row);
        setIsModalBudgetOpen(true);
      },
    },
    {
      label: content.budgeting.budgets.actions.delete,
      onClick: () => promptDeleteBudget(row),
    },
  ];

  const budgetRightElement = (
    <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
      <button
        className="cta-button secondary rounded"
        onClick={() => {
          setCreateBudgetError("");
          setIsCreateBudgetModalOpen(true);
        }}
      >
        {content.budgeting.budgets.create_button}
      </button>
    </div>
  );

  useEffect(() => {
    loadInitialData(true);
  }, [loadInitialData]);

  // Interceptor Gate 1: Full Onboarding Loader Card
  if (isEmptyOnboarding) {
    return (
      <InstitutionDataLoaderBanner
        content={content.status.transactions.no_data}
        refreshContent={content.global.refresh_in}
        onRefresh={loadSyncStates}
      />
    );
  }

  return (
    <Fragment>
      {/* Interceptor Gate 2: Partial History Sync Banner */}
      {isPartialSyncing && (
        <InstitutionPartialDataWarningBanner
          onRefresh={loadSyncStates}
          content={content.status.transactions.processing}
          processingText={processingInstitutionsText}
          refreshText={content.global.refresh}
        />
      )}

      <LoadingToRealTransitionWrapper
        isLoading={loading}
        ghost={
          <Fragment>
            <GhostOverallBudgetCard />
            <GhostCardContainer>
              <GhostDataTable
                headers={headers}
                rows={1}
                title={content.budgeting.budgets.title}
                showSearch={false}
                showAddButton={true}
              />
            </GhostCardContainer>
          </Fragment>
        }
      >
        <OverallBudgetCard
          spent={activeSummary.total_spent}
          limit={activeSummary.total_limit}
          percentage={activeSummary.percentage}
          copy={content.budgeting.overall_usage}
          hasNoBudgets={rows.length === 0}
          selectedFrequency={overallFrequency}
          onFrequencyChange={(freq) => setOverallFrequency(freq)}
          onCreateBudgetClick={() => {
            setCreateBudgetError("");
            setIsCreateBudgetModalOpen(true);
          }}
        />
        <div className="mobile-container-separator"></div>
        <DataTable
          title={content.budgeting.budgets.title}
          headers={headers}
          rows={filteredRows}
          emptyStateTitle={content.budgeting.budgets.empty_title}
          emptyStateText={
            rows.length === 0
              ? content.budgeting.budgets.empty_description
              : content.budgeting.budgets.filtered_empty
          }
          preserveStructureOnEmpty={rows.length > 0}
          getActionMenu={getBudgetActions}
          rightElement={budgetRightElement}
          filterElement={rows.length > 0}
          onFilterClick={handleFilterClick}
          copy={content.budgeting.budgets.spent_limit_tooltips}
        />
      </LoadingToRealTransitionWrapper>

      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        title={content.budgeting.filter.title}
        applyText={filterContent.apply}
      >
        <div className="filter-field">
          <p className="filter-label">{content.budgeting.filter.institution}</p>
          <select
            className="form-input"
            value={filters.institutions}
            onChange={(e) => handleFilterChange("institutions", e.target.value)}
          >
            <option value="">{filterContent.all_institutions}</option>
            {institutionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <p className="filter-label">{content.budgeting.filter.account}</p>
          <select
            className="form-input"
            value={filters.accounts}
            onChange={(e) => handleFilterChange("accounts", e.target.value)}
          >
            <option value="">{filterContent.all_accounts}</option>
            {accountOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <p className="filter-label">{content.budgeting.filter.category}</p>
          <select
            className="form-input"
            value={filters.categories}
            onChange={(e) => handleFilterChange("categories", e.target.value)}
          >
            <option value="">{filterContent.all_categories}</option>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <p className="filter-label">{content.budgeting.filter.frequency}</p>
          <select
            className="form-input"
            value={filters.frequency}
            onChange={(e) => handleFilterChange("frequency", e.target.value)}
          >
            <option value="">{content.budgeting.filter.all_frequencies}</option>
            {frequencyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </FilterModal>

      {isModalBudgetOpen && (
        <BudgetDetailsModal
          budget={selectedBudget}
          onClose={() => setIsModalBudgetOpen(false)}
          copy={content.budgeting.details}
        />
      )}
      {isCreateBudgetModalOpen && (
        <CreateBudgetModal
          onClose={() => {
            setCreateBudgetError("");
            setIsCreateBudgetModalOpen(false);
          }}
          onSave={handleSaveNewBudget}
          metadata={metadata}
          apiError={createBudgetError}
          isSubmitting={isSavingBudget}
          onClearApiError={() => setCreateBudgetError("")}
          copy={{
            ...content.budgeting.create_budget,
            frequency_options: content.budgeting.frequency_options,
            threshold_options: content.budgeting.threshold_options,
            errors: content.budgeting.create_budget.errors,
          }}
        />
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setDeleteError("");
          setIsDeleteModalOpen(false);
        }}
        onConfirm={confirmDelete}
        title={content.budgeting.delete_budget.title}
        message={content.budgeting.delete_budget.message}
        error={deleteError}
        confirmText={content.budgeting.delete_budget.confirm}
        cancelText={content.budgeting.delete_budget.cancel}
        confirmButtonClass="danger"
        isLoading={isDeleting}
      />
    </Fragment>
  );
}

export default PersonalBudgets;
